/**
 * Top-of-dashboard section: this week's due + undone WBS tasks, grouped
 * by parent phase → Backlog project key → assignee. Sourced from
 * `computeThisWeekDueByPhase` — see that helper for the filter contract
 * (kind === 'task', status ∉ {done,cancelled}, plannedEnd within Mon-Sun
 * of the week containing `now`, Backlog-linked only).
 *
 * Rendering choices:
 *   - Zero rows → single green banner, matching `OverdueTaskList`.
 *   - Each phase folds into a `<details>` (open by default) so PMs can
 *     collapse phases they don't own without re-flowing the whole page.
 *   - Backlog project keys sit as small tag-style headings; each
 *     assignee sub-block leads with the resolved assignee chip.
 *   - Rows read left-to-right: M/d(曜) · タイトル · Backlog リンク.
 *     StatusBadge only appears for `blocked` — every other kept row is
 *     "in-flight, no signal" so we keep the row visually quiet.
 */
import type { AssigneeDoc } from '@mct/shared';
import { format } from 'date-fns';
import type {
  ThisWeekDueAssigneeGroup,
  ThisWeekDuePhaseGroup,
  ThisWeekDueProjectGroup,
  ThisWeekDueTaskRow,
} from '../../lib/dashboard';
import { buildBacklogIssueUrl } from '../../lib/backlog';
import { AssigneeChip } from '../common/AssigneeChip';

const JA_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

export interface ThisWeekDueByPhaseProps {
  phases: readonly ThisWeekDuePhaseGroup[];
  assignees: readonly AssigneeDoc[];
  /** Project's Backlog space URL. `null` → fall back to `/view/{key}`. */
  spaceUrl: string | null;
}

export function ThisWeekDueByPhase({
  phases,
  assignees,
  spaceUrl,
}: ThisWeekDueByPhaseProps): JSX.Element {
  const assigneeById = new Map<string, AssigneeDoc>();
  for (const a of assignees) assigneeById.set(a.assigneeId, a);

  const totalCount = countRows(phases);
  if (totalCount === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-4 text-sm text-emerald-700">
        今週期限のタスクはありません
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {phases.map((phase) => (
        <PhaseCard
          key={phase.phaseItemId ?? '__no_phase__'}
          phase={phase}
          assigneeById={assigneeById}
          spaceUrl={spaceUrl}
        />
      ))}
    </div>
  );
}

function PhaseCard({
  phase,
  assigneeById,
  spaceUrl,
}: {
  phase: ThisWeekDuePhaseGroup;
  assigneeById: Map<string, AssigneeDoc>;
  spaceUrl: string | null;
}): JSX.Element {
  const count = phase.projectGroups.reduce(
    (sum, p) => sum + p.assigneeGroups.reduce((s, a) => s + a.rows.length, 0),
    0,
  );
  // Sort assignee groups by resolved display name so the visual order
  // matches how the PM reads the section, without disturbing the pure
  // aggregator's assigneeId ordering.
  const projectGroups = phase.projectGroups.map((project) => ({
    ...project,
    assigneeGroups: sortAssigneeGroupsByName(project.assigneeGroups, assigneeById),
  }));
  return (
    <details
      open
      className="group rounded-lg border border-slate-200 bg-white"
      data-testid={`this-week-phase-${phase.phaseItemId ?? 'no-phase'}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm">
        <span
          className="text-slate-400 transition-transform group-open:rotate-90"
          aria-hidden
        >
          ▶
        </span>
        <span className="font-semibold text-slate-800">{phase.phaseName}</span>
        <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 tabular-nums">
          {count} 件
        </span>
      </summary>
      <div className="flex flex-col gap-3 border-t border-slate-100 px-3 py-3">
        {projectGroups.map((project) => (
          <ProjectBlock
            key={project.backlogProjectKey}
            project={project}
            assigneeById={assigneeById}
            spaceUrl={spaceUrl}
          />
        ))}
      </div>
    </details>
  );
}

function ProjectBlock({
  project,
  assigneeById,
  spaceUrl,
}: {
  project: ThisWeekDueProjectGroup;
  assigneeById: Map<string, AssigneeDoc>;
  spaceUrl: string | null;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
          {project.backlogProjectKey}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {project.assigneeGroups.map((group) => (
          <AssigneeBlock
            key={group.assigneeId ?? '__unassigned__'}
            group={group}
            assigneeById={assigneeById}
            spaceUrl={spaceUrl}
          />
        ))}
      </div>
    </div>
  );
}

function AssigneeBlock({
  group,
  assigneeById,
  spaceUrl,
}: {
  group: ThisWeekDueAssigneeGroup;
  assigneeById: Map<string, AssigneeDoc>;
  spaceUrl: string | null;
}): JSX.Element {
  const assignee = group.assigneeId ? assigneeById.get(group.assigneeId) ?? null : null;
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/50 px-2 py-1.5">
      <div className="mb-1 flex items-center gap-2">
        <AssigneeChip assignee={assignee} />
        <span className="text-[11px] text-slate-500 tabular-nums">
          {group.rows.length} 件
        </span>
      </div>
      <ul className="m-0 flex flex-col gap-0.5 p-0">
        {group.rows.map((row) => (
          <TaskLine key={row.itemId} row={row} spaceUrl={spaceUrl} />
        ))}
      </ul>
    </div>
  );
}

function TaskLine({
  row,
  spaceUrl,
}: {
  row: ThisWeekDueTaskRow;
  spaceUrl: string | null;
}): JSX.Element {
  const url = buildBacklogIssueUrl(spaceUrl, row.backlogIssueKey) ?? `/view/${row.backlogIssueKey}`;
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
      <span className="w-16 shrink-0 text-[11px] tabular-nums text-slate-500">
        {format(row.plannedEnd, 'M/d')} ({JA_WEEKDAYS[row.plannedEnd.getDay()]})
      </span>
      <span className="min-w-0 flex-1 truncate text-slate-800" title={row.name}>
        {row.name}
      </span>
      {row.status === 'blocked' ? (
        <span className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
          ブロック
        </span>
      ) : null}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 text-[11px] font-medium text-sky-700 hover:underline"
      >
        {row.backlogIssueKey}
      </a>
    </li>
  );
}

function countRows(phases: readonly ThisWeekDuePhaseGroup[]): number {
  let n = 0;
  for (const p of phases) {
    for (const proj of p.projectGroups) {
      for (const a of proj.assigneeGroups) n += a.rows.length;
    }
  }
  return n;
}

function sortAssigneeGroupsByName(
  groups: readonly ThisWeekDueAssigneeGroup[],
  assigneeById: Map<string, AssigneeDoc>,
): ThisWeekDueAssigneeGroup[] {
  return [...groups].sort((a, b) => {
    // Unassigned always trails named assignees.
    if (a.assigneeId == null && b.assigneeId != null) return 1;
    if (a.assigneeId != null && b.assigneeId == null) return -1;
    if (a.assigneeId == null && b.assigneeId == null) return 0;
    const an = assigneeById.get(a.assigneeId as string)?.name ?? (a.assigneeId as string);
    const bn = assigneeById.get(b.assigneeId as string)?.name ?? (b.assigneeId as string);
    return an.localeCompare(bn, 'ja');
  });
}
